import React, { useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2, ClipboardList, Database, HeartPulse, LayoutDashboard,
  LogOut, Menu, Settings, Smartphone, Users, X,
} from 'lucide-react';

import { useAuthStore } from '../hooks/useAuth';
import Logo from './Logo';
import { hasPermission, type AppPermission } from '../lib/permissions';
import { cn } from '../lib/utils';
import { Button } from './ui/Button';
import { FarbschemaWahl } from './ui/FarbschemaWahl';
import { useFarbschema } from '../hooks/useFarbschema';

/**
 * Rahmen der Anwendung.
 *
 * Die Seitenleiste war eine flache Liste aus neun Punkten auf fast schwarzem
 * Grund. Bei neun gleich aussehenden Zeilen sucht man jedes Mal neu, und die
 * dunkle Flaeche zog das Auge staerker an als der Inhalt daneben.
 *
 * Jetzt hell, und die Punkte sind nach Arbeitszusammenhang gruppiert: was
 * taeglich gebraucht wird oben, Verwaltung darunter, das eigene Konto unten
 * beim Abmelden. Gruppen ohne sichtbaren Punkt verschwinden mitsamt ihrer
 * Ueberschrift - der Plattform-Administrator sieht sonst leere Abschnitte.
 */

type NavItem = {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  permission?: AppPermission;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Service',
    items: [
      { label: 'Service-Cockpit', path: '/', icon: LayoutDashboard },
      { label: 'Servicefälle', path: '/service-cases', icon: ClipboardList, permission: 'viewServiceCases' },
      { label: 'Technikermodus', path: '/technician', icon: Smartphone, permission: 'useTechnicianMode' },
    ],
  },
  {
    title: 'Anlagen',
    items: [
      { label: 'Anlagen', path: '/devices', icon: Database },
      { label: 'Anlagenzustand', path: '/anlagenzustand', icon: HeartPulse },
    ],
  },
  {
    title: 'Verwaltung',
    items: [
      { label: 'Kunden', path: '/customers', icon: Users, permission: 'manageCustomers' },
      { label: 'Team & Techniker', path: '/users', icon: Users, permission: 'manageTenantUsers' },
      { label: 'Mandanten', path: '/tenants', icon: Building2, permission: 'manageTenants' },
    ],
  },
];

/**
 * `children` ersetzt den `Outlet`.
 *
 * Gebraucht fuer die Fehlergrenze: Scheitert eine Seite, soll der Rahmen
 * stehen bleiben - Menue und Seitenleiste bleiben bedienbar, statt dass die
 * ganze Anwendung verschwindet.
 */
const AppLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [showDetailMenuButton, setShowDetailMenuButton] = React.useState(true);
  const lastScrollTopRef = React.useRef(0);
  const { schema: farbschema, setSchema: setFarbschema } = useFarbschema();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDeviceDetail = location.pathname.startsWith('/devices/');
  const appVersion = import.meta.env.VITE_APP_VERSION || 'v2.6-dev-chat';

  const sichtbar = (item: NavItem) => !item.permission || hasPermission(user, item.permission);
  const gruppen = NAV_GROUPS
    .map((gruppe) => ({ ...gruppe, items: gruppe.items.filter(sichtbar) }))
    .filter((gruppe) => gruppe.items.length > 0);

  const kontoEintrag: NavItem = { label: 'Mein Konto', path: '/account', icon: Settings };
  const alleEintraege = [...gruppen.flatMap((g) => g.items), kontoEintrag];

  /**
   * Die Unterseiten einer Anlage tragen alle den Pfad `/devices/<id>/…` und
   * hiessen deshalb samt und sonders "Anlagen-Detailansicht". Fuer die
   * Kopfzeile war das gerade noch vertretbar - fuer die Ansage nicht: Der
   * Text aenderte sich beim Wechsel zwischen Anlage, Chat, Akte und Bericht
   * **nicht**, und `aria-live` sagt nur Aenderungen an. Angesagt wurde also
   * gar nichts.
   *
   * Aufgefallen im Codex-Review zu PR #57.
   */
  const ANLAGEN_UNTERSEITEN: Array<[string, string]> = [
    ['/chat', 'KI-Einsatz-Chat'],
    ['/asset', 'Anlagenakte'],
    ['/report', 'Reporting'],
  ];

  const getPageTitle = () => {
    if (isDeviceDetail) {
      const treffer = ANLAGEN_UNTERSEITEN.find(([ende]) => location.pathname.endsWith(ende));
      return treffer ? treffer[1] : 'Anlagen-Detailansicht';
    }
    return alleEintraege.find((i) => i.path === location.pathname)?.label ?? 'Heizungsleser';
  };

  const istAktiv = (item: NavItem) =>
    location.pathname === item.path || (item.path === '/devices' && isDeviceDetail);

  React.useEffect(() => {
    lastScrollTopRef.current = 0;
    const frameId = window.requestAnimationFrame(() => setShowDetailMenuButton(true));
    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, isMobileMenuOpen]);

  const handleMainScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!isDeviceDetail || isMobileMenuOpen) return;
    const currentTop = event.currentTarget.scrollTop;
    const previousTop = lastScrollTopRef.current;
    if (currentTop <= 16) setShowDetailMenuButton(true);
    else if (currentTop > previousTop + 8) setShowDetailMenuButton(false);
    else if (currentTop < previousTop - 8) setShowDetailMenuButton(true);
    lastScrollTopRef.current = currentTop;
  };

  /**
   * Fokus und Ansage beim Seitenwechsel.
   *
   * Ein Wechsel innerhalb der Anwendung laedt die Seite nicht neu - fuer ein
   * Vorleseprogramm passiert also nichts Sichtbares: Es liest weiter, wo es
   * war, und der Fokus bleibt auf dem Menuepunkt, den man gerade angeklickt
   * hat. Hier wandert er auf den Inhaltsbereich, und der neue Seitenname wird
   * angesagt.
   *
   * `preventScroll`, weil der Fokus sonst die Seite an den Anfang zieht -
   * sichtbar als kurzes Springen.
   *
   * **Nicht beim ersten Laden.** Die erste Fassung tat das - und machte damit
   * die Sprungmarke unerreichbar: Der Fokus stand schon im Inhalt, der erste
   * Tastendruck fuehrte also am Sprunglink vorbei.
   *
   * Verglichen wird der **Pfad**, nicht ein Zaehler. Die zweite Fassung nahm
   * ein `useRef(true)` und setzte es beim ersten Lauf auf `false` - im Test
   * ging das gut, im Browser nicht: `StrictMode` ruft Effekte in der
   * Entwicklung **zweimal** auf, der erste Lauf kippte das Merkmal, der
   * zweite setzte den Fokus doch. Ein Pfadvergleich ist gegen den doppelten
   * Aufruf unempfindlich.
   */
  const inhalt = useRef<HTMLElement>(null);
  const letzterPfad = useRef(location.pathname);

  // Escape schliesst das Klappmenue - auf Fensterebene, nicht am Dialog:
  // Direkt nach dem Oeffnen steht der Fokus noch auf dem Oeffnen-Knopf in der
  // Kopfzeile, einem Geschwister des Dialogs. Ein Handler am Dialog bekam
  // dieses Escape nie zu sehen; es wirkte erst nach dem ersten Tab in einen
  // Eintrag. Gemeldet im Codex-Review zu PR #84.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const taste = (ereignis: KeyboardEvent) => {
      if (ereignis.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [isMobileMenuOpen]);

  // Fokus-Umzug: Beim Oeffnen wandert der Fokus in den Dialog, beim
  // Schliessen zurueck auf den Ausloeseknopf. Vorher blieb er beim Oeffnen
  // auf dem Knopf stehen (zulaessig, aber der erste Tab musste erst in die
  // Eintraege finden) - als Kuer notiert im Aenderungsdokument zu PR #84.
  //
  // Der Ausloeser wird nur uebernommen, wenn der Fokus NICHT schon im
  // Dialog steht: StrictMode ruft Effekte doppelt auf, und der zweite Lauf
  // haette sonst den Dialog selbst als Ruecksprungziel gemerkt - der beim
  // Schliessen nicht mehr existiert. Kein Ruecksprung, wenn der Ausloeser
  // inzwischen aus dem Dokument verschwunden ist (Schwebeknopf der
  // Detailansicht blendet sich aus) - dann greift die Seitenwechsel-Logik
  // darunter, die den Inhalt fokussiert.
  const klappmenue = useRef<HTMLDivElement>(null);
  const menueAusloeser = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isMobileMenuOpen) {
      const aktiv = document.activeElement;
      if (aktiv instanceof HTMLElement && !klappmenue.current?.contains(aktiv)) {
        menueAusloeser.current = aktiv;
      }
      klappmenue.current?.focus({ preventScroll: true });
      return;
    }
    if (menueAusloeser.current) {
      if (menueAusloeser.current.isConnected) {
        menueAusloeser.current.focus({ preventScroll: true });
      }
      menueAusloeser.current = null;
    }
  }, [isMobileMenuOpen]);

  // Waechst das Fenster ueber den lg-Umbruch, schliesst das Klappmenue von
  // allein. Ohne das bliebe der Inhalt als inert zurueck, obwohl gar keine
  // Ueberlagerung mehr zu sehen ist (die traegt lg:hidden).
  // jsdom kennt matchMedia nicht - deshalb der Funktionstest.
  useEffect(() => {
    if (!isMobileMenuOpen || typeof window.matchMedia !== 'function') return;
    const lg = window.matchMedia('(min-width: 1024px)');
    const schliessen = () => {
      if (lg.matches) setIsMobileMenuOpen(false);
    };
    schliessen();
    lg.addEventListener('change', schliessen);
    return () => lg.removeEventListener('change', schliessen);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (letzterPfad.current !== location.pathname) {
      letzterPfad.current = location.pathname;
      inhalt.current?.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  const eintragKlassen = (aktiv: boolean) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
      aktiv
        ? 'bg-brand-50 font-semibold text-brand-700'
        : 'font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    );

  return (
    <div className="flex h-screen min-h-screen flex-col overflow-hidden bg-slate-50 lg:flex-row">
      {/*
        Sprungmarke.

        Bis 2026-08-13 begann die Tastatur bei *jedem* Seitenwechsel wieder
        ganz oben: erst durch das Menue, dann durch die Seitenleiste, dann
        endlich zum Inhalt. Bei zehn Menuepunkten sind das zehn Tabstopps -
        auf jeder Seite, jedes Mal.

        Der Link ist unsichtbar, bis er den Fokus bekommt. Er ist das erste
        fokussierbare Element der Seite.
      */}
      <a
        href="#inhalt"
        inert={isMobileMenuOpen || undefined}
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-akzent focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-auf-akzent"
      >
        Zum Inhalt springen
      </a>
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <Logo className="h-8" />
        </div>

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {gruppen.map((gruppe) => (
            <div key={gruppe.title} className="space-y-1">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {gruppe.title}
              </p>
              {gruppe.items.map((item) => {
                const aktiv = istAktiv(item);
                return (
                  <Link key={item.path} to={item.path} className={eintragKlassen(aktiv)}>
                    <item.icon className={cn('h-4 w-4 shrink-0', aktiv ? 'text-brand-600' : 'text-slate-400')} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-3">
          {/* Die Wahl steht hier und nicht unter "Mein Konto": Sie gilt fuer
              dieses Geraet, nicht fuer die Person - und sie soll ohne Umweg
              ueber eine Unterseite erreichbar sein. */}
          <FarbschemaWahl schema={farbschema} onChange={setFarbschema} className="mb-3" />
          <Link to="/account" className={eintragKlassen(istAktiv(kontoEintrag))}>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-600">
              {(user?.full_name || user?.email || '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate">{user?.full_name || user?.email}</span>
              <span className="block truncate text-xs font-normal text-slate-400">Mein Konto</span>
            </span>
          </Link>
          {/* Bewusst ein rohes `button`: "Abmelden" steht als letzter Eintrag
              in der Navigationsspalte und muss aussehen wie die `Link`-Eintraege
              darueber. Ein Knopf-Baustein wuerde es aus der Reihe heben. */}
          <button
            type="button"
            onClick={handleLogout}
            className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <LogOut className="h-4 w-4 shrink-0 text-slate-400" />
            Abmelden
          </button>
          <p className="px-3 pt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">{appVersion}</p>
        </div>
      </aside>

      {!isDeviceDetail && (
        <header className="sticky top-0 z-[60] flex items-center justify-between border-b border-slate-200 bg-white p-4 lg:hidden">
          <Logo iconOnly className="h-8" />
          <Button
            tone="ghost"
            size="icon"
            className="h-11 w-11"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="text-slate-700" /> : <Menu className="text-slate-700" />}
          </Button>
        </header>
      )}

      {/* Bewusst ein rohes `button`: fest positionierter Schwebeknopf mit
          eigener Ein- und Ausblendung - der Baustein bringt dafuer nichts mit. */}
      {isDeviceDetail && (
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={cn(
            'fixed right-3 top-3 z-[70] flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-md transition-all duration-200 lg:hidden',
            showDetailMenuButton ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0',
          )}
          aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      )}

      {isMobileMenuOpen && (
        /* Fuer die Tastatur ein Dialog: Der Inhalt darunter ist inert (siehe
           main), fokussierbar bleiben nur der Schliessknopf in der Kopfzeile
           und die Eintraege hier. Vorher wanderte der Fokus hinter die
           Ueberlagerung in verdeckte Elemente - gemeldet im Codex-Review zu
           PR #81. */
        <div
          ref={klappmenue}
          role="dialog"
          aria-modal="true"
          aria-label="Hauptnavigation"
          tabIndex={-1}
          className={cn('fixed inset-0 z-50 overflow-y-auto bg-white px-5 focus:outline-none lg:hidden', isDeviceDetail ? 'pt-8' : 'pt-20')}
        >
          <nav className="space-y-6 pb-8">
            {gruppen.map((gruppe) => (
              <div key={gruppe.title} className="space-y-1">
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {gruppe.title}
                </p>
                {gruppe.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(eintragKlassen(istAktiv(item)), 'px-4 py-3 text-base')}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
            <div className="space-y-1 border-t border-slate-200 pt-4">
              {/* Auch hier, nicht nur in der Seitenleiste: Die steht unterhalb
                  von `md` auf `hidden`, und damit haette auf dem Telefon
                  niemand eine Moeglichkeit, das Farbschema zu waehlen -
                  ausgerechnet dort, wo die Wahl geraetegebunden am meisten
                  Sinn ergibt. Gemeldet im Codex-Review zu PR #70. */}
              <FarbschemaWahl schema={farbschema} onChange={setFarbschema} className="mb-3" />
              <Link
                to="/account"
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(eintragKlassen(istAktiv(kontoEintrag)), 'px-4 py-3 text-base')}
              >
                <Settings className="h-5 w-5 shrink-0" />
                Mein Konto
              </Link>
              {/* Bewusst ein rohes `button`, wie oben im Seitenmenue:
                  "Abmelden" steht als letzter Eintrag in der Navigationsliste
                  und muss aussehen wie die Links darueber. */}
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-base font-medium text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <LogOut className="h-5 w-5 shrink-0 text-slate-400" />
                Abmelden
              </button>
            </div>
          </nav>
        </div>
      )}

      <main
        ref={inhalt}
        id="inhalt"
        tabIndex={-1}
        inert={isMobileMenuOpen || undefined}
        className="flex h-full min-w-0 flex-1 flex-col overflow-hidden focus:outline-none"
      >
        <header className="hidden h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-8 lg:flex">
          <h2 className="text-sm font-medium text-slate-500">{getPageTitle()}</h2>
        </header>
        {/* Nur fuer Vorleseprogramme: der Name der Seite, auf der man
            gerade gelandet ist. */}
        {/* Der Titel steht direkt hier, nicht in einem Zustand: `aria-live`
            sagt ohnehin nur *Aenderungen* an, und ein `setState` im Effekt
            loest eine zweite Darstellung aus, ohne etwas zu gewinnen. */}
        <p aria-live="polite" className="sr-only">{getPageTitle()}</p>
        <div className="flex-1 overflow-auto scroll-smooth p-4 md:p-8" onScroll={handleMainScroll}>
          {children ?? <Outlet />}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
